import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Flex, xcss } from '@atlaskit/primitives';
import NodeIcon from '@atlaskit/icon/core/node';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';

export const SOURCE_NAME = 'contentTreeOnboarding';

const nav3OnboardingRowContainer = xcss({
	marginLeft: 'space.100',
});

const nav4OnboardingItemContainer = xcss({
	marginLeft: 'space.025',
});

const css = {
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	font: token('font.body'),
	userSelect: 'none',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: 36,
	boxSizing: 'border-box',
	textDecoration: 'none',
	color: token('color.text.subtle'),
	paddingLeft: '26px',
	'&:visited, &:active, &:focus, &:hover': {
		color: token('color.text.subtle'),
		textDecoration: 'none',
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:active': {
		backgroundColor: token('color.background.selected'),
		boxShadow: 'none',
		border: 'none',
	},
	'&:focus': {
		outline: 'none',
		boxShadow: `${token('color.border.selected')} 0px 0px 0px 2px inset`,
		backgroundColor: token('color.background.selected'),
	},
};

type ContentTreeOnboardingItemProps = React.PropsWithChildren<{
	contentType: string;
	subType?: string;
	href: string;
	iconGlyph: React.ReactNode;
}>;

export const ContentTreeOnboardingItem = ({
	children,
	contentType,
	subType,
	href,
	iconGlyph,
}: ContentTreeOnboardingItemProps) => {
	const isNav4Enabled = useIsNav4Enabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'contentTreeOnboarding',
	});

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'contentTreeOnboardingItem',
				source: SOURCE_NAME,
				attributes: {
					contentType,
					subType,
				},
			},
		}).fire();
	};

	if (isNav4Enabled) {
		return (
			<LinkMenuItem
				elemBefore={<NodeIcon label="" />}
				href={shouldBlockCreate ? '#' : href}
				onClick={enforceStorageLimit(onClick)}
			>
				<Inline space="space.100" alignBlock="center" xcss={nav4OnboardingItemContainer}>
					<ContentIcon type={contentType} subType={subType} />
					{children}
				</Inline>
			</LinkMenuItem>
		);
	}

	return (
		<Box xcss={nav3OnboardingRowContainer}>
			<ContentTreeLinkItem
				href={shouldBlockCreate ? undefined : href}
				onClick={enforceStorageLimit(onClick)}
			>
				<Flex>{iconGlyph}</Flex>
				{children}
			</ContentTreeLinkItem>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/design-system/no-html-anchor, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ContentTreeLinkItem = styled.a(css);
